<script lang="ts">
import { computed, defineComponent, provide, ref, watch } from 'vue'
import { useDark } from '@vueuse/core'
import { useRoute, useRouter, type LocationQueryRaw } from 'vue-router'

import ConfirmDialog from 'primevue/confirmdialog'

import AppSidebar from '@/components/atoms/sidebars/AppSidebar.vue'
import AppSidebarMobile from '@/components/atoms/sidebars/AppSidebarMobile.vue'
import AppHeader from '@/components/molecules/header/AppHeader.vue'
import WebphoneDraggable from '@/components/molecules/webphone-draggable/WebphoneDraggable.vue'
import VideoCall from '@/components/molecules/video-call/VideoCall.vue'

import { useAuthStore } from '@/stores/useAuthStore'
import { useWorkspaceStore } from '@/stores/useWorkspaceStore'
import { useNoteServices } from '@/services/notes/useNoteServices'

export default defineComponent({
  components: {
    AppSidebar,
    AppSidebarMobile,
    ConfirmDialog,
    AppHeader,
    WebphoneDraggable,
    VideoCall,
  },
  setup() {
    const isSidebarExpanded = ref(false)
    provide('isSidebarExpanded', isSidebarExpanded)

    const workspace = useWorkspaceStore()
    const authStore = useAuthStore()
    const noteService = useNoteServices()
    const isDark = useDark({ selector: 'html' })
    const autoOpenedNotesCallKey = ref('')

    const route = useRoute()
    const router = useRouter()
    const currentPath = computed(() => route.path)
    const normalizePhone = (value?: string | null) => (value ?? '').replace(/\D/g, '')
    const isLikelyExtensionNumber = (value?: string | null) => {
      const normalized = normalizePhone(value)
      if (!normalized) return true

      const ownExtension = normalizePhone(authStore.webphoneNumber)
      if (ownExtension && normalized === ownExtension) return true

      return normalized.length <= 5
    }
    const resolveCurrentCallNumber = () => {
      const candidates = [
        authStore.webphoneTarget,
        authStore.webphoneRemoteNumber,
        workspace.number,
        workspace.lastNumber,
      ]
        .map((value) => value?.trim() ?? '')
        .filter(Boolean)

      return candidates.find((value) => !isLikelyExtensionNumber(value)) ?? candidates[0] ?? ''
    }
    const currentCallNumber = computed(resolveCurrentCallNumber)

    const hasSamePhone = (left?: string | null, right?: string | null) => {
      const normalizedLeft = normalizePhone(left)
      const normalizedRight = normalizePhone(right)
      if (!normalizedLeft || !normalizedRight) return false
      if (normalizedLeft === normalizedRight) return true

      const shortestLength = Math.min(normalizedLeft.length, normalizedRight.length)
      if (shortestLength < 8) return false

      return normalizedLeft.endsWith(normalizedRight) || normalizedRight.endsWith(normalizedLeft)
    }

    const goToCallNotesWhenAvailable = async (number: string) => {
      const normalizedNumber = normalizePhone(number)
      if (!normalizedNumber) return

      const callKey = normalizedNumber
      if (autoOpenedNotesCallKey.value === callKey) return
      autoOpenedNotesCallKey.value = callKey

      try {
        const response = await noteService.getNotes()
        const hasMatchingNote = response.data.some((note) => hasSamePhone(note.number, number))
        if (hasMatchingNote) {
          const nextQuery: LocationQueryRaw = {
            ...(route.path === '/' ? route.query : {}),
            panel: 'notes',
          }
          void router.replace({ path: '/', query: nextQuery })
        }
      } catch {
        autoOpenedNotesCallKey.value = ''
      }
    }

    watch(
      () => route.path,
      (nextPath, previousPath) => {
        if (!previousPath || nextPath === previousPath) return
        if (authStore.isWebphoneInCall) {
          authStore.showWebphone = nextPath === '/'
          return
        }

        authStore.showWebphone = nextPath === '/'
      },
    )

    watch(
      () => [authStore.isWebphoneInCall, currentCallNumber.value] as const,
      ([isInCall, number]) => {
        if (!isInCall) {
          autoOpenedNotesCallKey.value = ''
          return
        }

        if (!number) return
        void goToCallNotesWhenAvailable(number)
      },
      { immediate: true },
    )

    return {
      isSidebarExpanded,
      isDark,
      currentPath,
      workspace,
    }
  },
})
</script>

<template>
  <div
    class="app-dashboard-shell flex min-h-screen w-full"
    :class="{ 'dashboard-shell--dark': isDark }"
  >
    <div class="relative z-10 hidden lg:block h-full">
      <AppSidebar @update:expanded="isSidebarExpanded = $event" />
    </div>

    <div class="lg:hidden w-full h-14 z-50 fixed top-0 left-0">
      <AppSidebarMobile />
    </div>

    <section
      class="relative z-10 flex-1 transition-all duration-300 h-screen overflow-y-auto"
      :class="{ 'lg:ml-50': isSidebarExpanded, 'lg:ml-16': !isSidebarExpanded }"
    >
      <AppHeader />
      <div class="lg:mt-0 mt-14">
        <div class="py-3 px-4 w-full mx-auto">
          <transition name="page" mode="out-in">
            <div :key="currentPath" class="app-subtle-enter">
              <slot />
            </div>
          </transition>
        </div>
      </div>
    </section>

    <WebphoneDraggable />
    <VideoCall v-model="workspace.isVideoCallVisible" />
    <ConfirmDialog />
  </div>
</template>

<style scoped>
.page-enter-active,
.page-leave-active {
  transition:
    opacity 0.2s ease,
    transform 0.38s ease;
}
.page-enter-from {
  opacity: 0;
  transform: translateY(15px);
}
.page-leave-to {
  opacity: 0;
  transform: translateY(-15px);
}
</style>
